import EditorialBriefDetails from "../EditorialBriefDetails";
import { useState, useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import api, { apiErr, fmtTime, scriptCostSeconds, recommendationsCostSeconds } from "../../lib/api";
import { Card, btnPrimary, CostBadge } from "../ui-bits";
import { toast } from "sonner";
import { Sparkles, Loader2, Wand2, Lightbulb, Eye, X, Clock, ChevronLeft, ChevronRight, Settings2, CalendarDays, Trash2 } from "lucide-react";
import { useQuota } from "../../context/QuotaContext";

const todayISO = () => new Date().toISOString().slice(0, 10);

// Une cada idea con su entrada de calendario si ya se programó una: por id (ideas nuevas, ver
// backend) o por título en minúsculas (respaldo para ideas generadas antes de que existiera el
// id — ver channel_recommendations en el backend, que hace backfill de id en cuanto se vuelven
// a cargar, pero mientras tanto el título sigue siendo la única correspondencia posible).
function findScheduledEntry(idea, calEntries) {
  if (!idea) return null;
  const titleKey = (idea.titulo || "").trim().toLowerCase();
  return (calEntries || []).find((e) => {
    if (!e.idea) return false;
    if (idea.id && e.idea.id) return e.idea.id === idea.id;
    return (e.idea.titulo || "").trim().toLowerCase() === titleKey && !!titleKey;
  }) || null;
}

export default function RecommendedVideos({ channel, openProject, loadVideos, onRecommendations, onOpenConfig, onOneClick }) {
  const { t } = useTranslation();
  const { quota } = useQuota();
  const isFree = (quota?.plan || "gratis") === "gratis" && !quota?.has_spendable_credits;
  const maxMinutes = quota?.max_minutes || 40;
  const minMinutes = Math.min(10, maxMinutes);
  const [ideas, setIdeas] = useState(channel.recommendations || null);
  const [loading, setLoading] = useState(false);
  const [generatingId, setGeneratingId] = useState(null);
  const [openIdea, setOpenIdea] = useState(null);
  const [duration, setDuration] = useState(Math.min(20, maxMinutes));
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 10;
  // Entradas de calendario del canal — solo para saber qué ideas ya están programadas y en qué
  // fecha (ver findScheduledEntry). CalendarPanel es quien de verdad gestiona el calendario;
  // aquí solo se lee, nunca se pisa nada de lo que ese componente ya hace.
  const [calEntries, setCalEntries] = useState([]);
  const [scheduleDate, setScheduleDate] = useState(todayISO());
  const [schedBusy, setSchedBusy] = useState(false);

  const loadCalEntries = () => {
    api.get(`/channels/${channel.id}/calendar`).then(({ data }) => setCalEntries(data || [])).catch(() => {});
  };
  useEffect(() => { loadCalEntries(); }, [channel.id]); // eslint-disable-line

  useEffect(() => { setIdeas(channel.recommendations || null); }, [channel.id, channel.recommendations]);

  const pageCount = ideas ? Math.ceil(ideas.length / PAGE_SIZE) : 0;
  useEffect(() => { if (page > 0 && page >= pageCount) setPage(Math.max(0, pageCount - 1)); }, [pageCount]); // eslint-disable-line
  const pagedIdeas = ideas ? ideas.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE) : [];

  const recMinutes = (idea) => {
    if (!idea) return Math.min(20, maxMinutes);
    if (idea.duracion_min) return Math.min(maxMinutes, Math.max(minMinutes, idea.duracion_min));
    const str = String(idea.duracion_sugerida || "");
    const m = str.match(/(\d+)/);
    let v = m ? parseInt(m[1], 10) : 20;
    if (/hora/i.test(str)) v *= 60;
    return Math.min(maxMinutes, Math.max(minMinutes, v));
  };

  useEffect(() => { if (openIdea) { setDuration(recMinutes(openIdea)); setScheduleDate(todayISO()); } }, [openIdea]);

  const hasRefs = (channel.reference_channels || []).length > 0;

  const scheduleIdea = async (idea) => {
    setSchedBusy(true);
    try {
      await api.post(`/channels/${channel.id}/calendar`, { date: scheduleDate, title: idea.titulo, idea });
      toast.success(t("rec.scheduledOk"));
      loadCalEntries();
    } catch (e) { toast.error(apiErr(e)); } finally { setSchedBusy(false); }
  };

  const unscheduleIdea = async (entryId) => {
    setSchedBusy(true);
    try {
      await api.delete(`/channels/${channel.id}/calendar/${entryId}`);
      toast.success(t("rec.unscheduledOk"));
      loadCalEntries();
    } catch (e) { toast.error(apiErr(e)); } finally { setSchedBusy(false); }
  };

  const analyze = async () => {
    setLoading(true);
    const prevCount = (ideas || []).length;
    try {
      const { data } = await api.post(`/channels/${channel.id}/recommendations`);
      const all = data.ideas || [];
      setIdeas(all);
      onRecommendations && onRecommendations(all);
      const added = data.added ?? (all.length - prevCount);
      if (!all.length) toast.info(t("rec.noIdeas"));
      else if (prevCount > 0) {
        if (added > 0) { toast.success(t("rec.ideasAdded", { count: added })); setPage(Math.ceil(all.length / PAGE_SIZE) - 1); }
        else toast.info(t("rec.noNewIdeas"));
      }
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setLoading(false); }
  };

  const generateScript = async (idea) => {
    setGeneratingId(idea.titulo);
    try {
      await api.post(`/channels/${channel.id}/generate-script`, { ...idea, duracion_min: duration });
      toast.success(t("rec.scriptStarted", { min: duration }));
      setOpenIdea(null);
      await loadVideos();
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setGeneratingId(null); }
  };

  return (
    <div className="space-y-4" data-testid="recommended-section">
      {hasRefs && (
        <div className="flex justify-end">
          <button data-testid="analyze-recommendations-btn" onClick={analyze} disabled={loading} className={`${btnPrimary} w-full sm:w-auto bg-brand-600 hover:bg-brand-700`}>
            {loading ? <Loader2 className="animate-spin" size={20} /> : <Wand2 size={20} />}
            {ideas && ideas.length ? t("rec.generateMore") : t("rec.analyze")}
            {!loading && <CostBadge seconds={recommendationsCostSeconds()} tip={t("rec.analyzeTip")} />}
          </button>
        </div>
      )}

      {!hasRefs && (
        <Card className="p-5 border-amber-200 bg-amber-50" data-testid="no-refs-warning">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <Lightbulb size={30} className="text-amber-500 shrink-0" />
            <div className="flex-1">
              <h3 className="text-base font-bold text-slate-900">{t("rec.noRefsTitle")}</h3>
              <p className="text-sm text-slate-600"><Trans i18nKey="rec.noRefs" components={{ 0: <b /> }} /></p>
            </div>
            <button data-testid="open-config-refs-btn" onClick={() => onOpenConfig && onOpenConfig()} className={`${btnPrimary} sm:w-auto whitespace-nowrap`}>
              <Settings2 size={20} /> {t("rec.configureRefs")}
            </button>
          </div>
        </Card>
      )}

      {loading ? (
        <Card className="p-8 flex items-center justify-center gap-3 text-slate-500"><Loader2 className="animate-spin" size={22} /> {t("rec.analyzing")}</Card>
      ) : ideas && ideas.length > 0 ? (
        <>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5" data-testid="ideas-grid">
          {pagedIdeas.map((idea, i) => {
            const gi = page * PAGE_SIZE + i;
            const scheduled = findScheduledEntry(idea, calEntries);
            return (
            <Card key={gi} data-testid={`idea-card-${gi}`} onClick={() => setOpenIdea(idea)}
              className="p-0 overflow-hidden flex flex-col cursor-pointer transition-all duration-200 group hover:border-amber-300 hover:shadow-md">
              <div className="relative aspect-video bg-gradient-to-br from-amber-50 to-orange-100 flex items-center justify-center p-4">
                <Sparkles size={18} className="absolute top-2 left-2 text-amber-400" />
                <h3 className="text-base font-bold text-slate-900 leading-snug text-center line-clamp-4">{idea.titulo}</h3>
              </div>
              <div className="p-4">
                <div className="text-sm text-slate-500 truncate">{idea.duracion_sugerida || "—"}</div>
                {scheduled && (
                  <div data-testid={`idea-scheduled-${gi}`} className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full px-2 py-0.5">
                    <CalendarDays size={12} /> {t("rec.scheduledFor", { date: new Date(scheduled.date + "T00:00:00").toLocaleDateString(undefined, { dateStyle: "medium" }) })}
                  </div>
                )}
                <div className="mt-2 inline-flex items-center gap-1.5 text-sm font-bold text-blue-700 group-hover:text-blue-900"><Eye size={16} /> {t("rec.viewDetail")}</div>
              </div>
            </Card>
            );
          })}
        </div>
        {pageCount > 1 && (
          <div className="flex items-center justify-center gap-3 pt-2" data-testid="ideas-paginator">
            <button data-testid="ideas-prev-page" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border-2 border-slate-200 text-slate-700 font-semibold hover:border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed">
              <ChevronLeft size={18} /> {t("rec.prevPage")}
            </button>
            <span className="text-sm font-semibold text-slate-600" data-testid="ideas-page-info">{t("rec.pageInfo", { page: page + 1, total: pageCount })}</span>
            <button data-testid="ideas-next-page" onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} disabled={page >= pageCount - 1}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border-2 border-slate-200 text-slate-700 font-semibold hover:border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed">
              {t("rec.nextPage")} <ChevronRight size={18} />
            </button>
          </div>
        )}
        </>
      ) : !hasRefs ? (
        <></>
      ) : ideas ? (
        <Card className="p-6 text-center text-slate-600">{t("rec.regenPrompt")}</Card>
      ) : (
        <Card className="p-6 text-center text-slate-600"><Trans i18nKey="rec.analyzePrompt" components={{ 0: <b /> }} /></Card>
      )}

      {openIdea && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50" onClick={() => setOpenIdea(null)} data-testid="idea-modal">
          <div className="bg-white rounded-2xl border-2 border-slate-200 shadow-2xl w-full max-w-2xl max-h-[88vh] overflow-y-auto vb-scroll" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4 p-6 pb-4 border-b border-slate-100 sticky top-0 bg-white">
              <div className="flex items-start gap-2">
                <Sparkles size={24} className="text-amber-500 mt-1 shrink-0" />
                <h3 className="text-2xl font-bold text-slate-900 leading-snug">{openIdea.titulo}</h3>
              </div>
              <button data-testid="idea-modal-close" onClick={() => setOpenIdea(null)} className="text-slate-400 hover:text-slate-700 shrink-0"><X size={26} /></button>
            </div>
            <div className="p-6 space-y-4">
              {openIdea.patron_titulo && (
                <div className="text-base text-blue-700 bg-blue-50 rounded-lg px-4 py-3 flex items-start gap-2">
                  <Sparkles size={17} className="mt-0.5 shrink-0" /> <span><b>{t("rec.suggestedTitle")}</b> {openIdea.patron_titulo}</span>
                </div>
              )}
              {openIdea.angulo && (
                <div>
                  <div className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-1">{t("rec.angle")}</div>
                  <p className="text-base text-slate-700">{openIdea.angulo}</p>
                </div>
              )}
              {openIdea.por_que_funciona && (
                <div className="text-base text-emerald-800 bg-emerald-50 rounded-lg px-4 py-3">
                  <b>{t("rec.whyWorks")}</b> {openIdea.por_que_funciona}
                </div>
              )}
              <EditorialBriefDetails idea={openIdea} />
              <div className="bg-slate-50 border-2 border-slate-200 rounded-xl px-4 py-4" data-testid="idea-duration-control">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-base font-bold text-slate-800">{t("rec.durationLabel")}</label>
                  <span className="text-lg font-bold text-blue-700" data-testid="idea-duration-value">{duration} min</span>
                </div>
                <input data-testid="idea-duration-slider" type="range" min={minMinutes} max={maxMinutes} step="1" value={duration}
                  onChange={(e) => setDuration(parseInt(e.target.value, 10))}
                  className="w-full h-3 accent-blue-600 cursor-pointer" />
                <div className="flex justify-between text-xs text-slate-400 mt-1"><span>{minMinutes} min</span><span>{maxMinutes} min</span></div>
                <p className="text-sm text-slate-500 mt-2">{t("rec.recommended", { min: recMinutes(openIdea) })}</p>
              </div>
              {(() => {
                const scheduled = findScheduledEntry(openIdea, calEntries);
                if (scheduled) {
                  return (
                    <div data-testid="idea-schedule-control" className="bg-indigo-50 border-2 border-indigo-200 rounded-xl px-4 py-4 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 text-indigo-800 font-semibold">
                        <CalendarDays size={18} />
                        {t("rec.scheduledFor", { date: new Date(scheduled.date + "T00:00:00").toLocaleDateString(undefined, { dateStyle: "medium" }) })}
                      </div>
                      <button data-testid="idea-unschedule-btn" onClick={() => unscheduleIdea(scheduled.id)} disabled={schedBusy}
                        className="inline-flex items-center gap-1.5 text-sm font-bold text-red-600 hover:text-red-800 disabled:opacity-50">
                        <Trash2 size={16} /> {t("rec.unschedule")}
                      </button>
                    </div>
                  );
                }
                return (
                  <div data-testid="idea-schedule-control" className="bg-slate-50 border-2 border-slate-200 rounded-xl px-4 py-4">
                    <label className="text-base font-bold text-slate-800 mb-2 flex items-center gap-2"><CalendarDays size={18} /> {t("rec.scheduleLabel")}</label>
                    <div className="flex gap-2">
                      <input data-testid="idea-schedule-date" type="date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)}
                        className="flex-1 border-2 border-slate-300 rounded-lg px-3 py-2.5 text-base outline-none focus:border-blue-600" />
                      <button data-testid="idea-schedule-btn" onClick={() => scheduleIdea(openIdea)} disabled={schedBusy}
                        className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-indigo-600 text-white text-base font-bold hover:bg-indigo-700 disabled:opacity-50">
                        {schedBusy ? <Loader2 size={18} className="animate-spin" /> : <CalendarDays size={18} />} {t("rec.addToCalendar")}
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="p-6 pt-2 sticky bottom-0 bg-white border-t border-slate-100 space-y-2">
              {onOneClick && !isFree && (
                <button
                  data-testid="idea-modal-one-click"
                  onClick={() => { const idea = openIdea; setOpenIdea(null); onOneClick(idea); }}
                  disabled={generatingId !== null}
                  className="w-full min-h-[48px] px-4 sm:px-5 text-base font-bold rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  <Wand2 size={20} /> {t("rec.oneClickCreate")}
                </button>
              )}
              <button data-testid="idea-modal-generate" onClick={() => generateScript(openIdea)} disabled={generatingId !== null} className={`w-full ${btnPrimary}`}>
                {generatingId === openIdea.titulo ? <Loader2 className="animate-spin" size={20} /> : <Wand2 size={20} />}
                {generatingId === openIdea.titulo ? t("rec.generating") : t("rec.generateScript", { min: duration })}
                {generatingId !== openIdea.titulo && <CostBadge seconds={scriptCostSeconds(duration)} tip={t("rec.scriptCostTip")} />}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
